import { Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  ReactElement,
  FC,
  useContext,
  useState,
  useRef,
  useEffect,
} from 'react';
import { AccountContext, RoleType } from 'auth/accountProvider';
import { useIsMounted } from 'usehooks-ts';
import { useProtectedRoute } from 'auth/useProtectedRoute';

/** Terminal destination for role denials. Must never itself be role-gated. */
export const NO_ACCESS_PATH = '/no-access';

interface IProtectedRouteProps {
  roles?: RoleType[];
  children?: ReactElement;
}

export const ProtectedRoute: FC<IProtectedRouteProps> = ({
  roles,
  children,
}) => {
  const location = useLocation();
  const [isSessChecked, setIsSessChecked] = useState<boolean>(false);
  const accountContext = useContext(AccountContext);
  const accountContextRef = useRef(accountContext);
  const { isAuthenticated, logout, hasRole } = accountContext;
  const isMounted = useIsMounted();

  // Keep ref in sync so useProtectedRoute sees the latest context after logout.
  useEffect(() => {
    accountContextRef.current = accountContext;
  }, [accountContext]);

  const Spinner = (
    <div className="absolute-center">
      <div className="d-flex justify-content-center">
        <span className="spinner spinner-default-grey" />
      </div>
    </div>
  );

  useProtectedRoute(
    logout,
    accountContextRef,
    setIsSessChecked,
    isAuthenticated,
    isMounted
  );

  /*
   * When user tries to access protected route:
   * Shows spinner while getting session data;
   * Redirects to Log-in if not authenticated;
   * Redirects to /no-access if required user roles not found;
   * Shows content.
   */
  if (!isAuthenticated && isSessChecked) {
    return (
      <Navigate to="/login" replace state={{ referer: location.pathname }} />
    );
  }
  if (isAuthenticated) {
    if (!roles || hasRole(roles)) {
      return children || <Outlet />;
    }
    // Deny to /no-access, never to "/". "/" is itself gated on VIEWER_ROLES,
    // so redirecting there would fail the same gate and redirect again — an
    // unbounded loop that hangs the tab and buries the logout control.
    // /no-access is registered outside every role gate, so this terminates.
    return <Navigate to={NO_ACCESS_PATH} replace />;
  }
  return Spinner;
};
